import { Component, computed, effect, ElementRef, OnDestroy, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import RTSPtoWEBPlayer from '@playerscripts/rtsp-to-web-player.js';
import { CallInfo, CallState, VoipService } from '@core/services/websockets/voip/voip.service';

@Component({
  selector: 'app-voip-call',
  standalone: true,
  imports: [],
  templateUrl: './voip-call.component.html',
  styleUrl: './voip-call.component.scss',
})
export class VoipCallComponent implements OnDestroy {
  protected readonly callState = toSignal(this.voip.callState$, { initialValue: 'idle' as CallState });
  protected readonly incomingCall = toSignal(this.voip.incomingCall$, { initialValue: null as CallInfo | null });
  protected readonly statusMessage = toSignal(this.voip.statusMessage$, { initialValue: '' });

  private readonly remoteStream = toSignal(this.voip.remoteStream$, { initialValue: null as MediaStream | null });
  private readonly audioRef = viewChild<ElementRef<HTMLAudioElement>>('remoteAudio');
  private readonly ringtoneRef = viewChild<ElementRef<HTMLAudioElement>>('ringtone');
  private readonly videoContainer = viewChild<ElementRef<HTMLDivElement>>('videoContainer');

  private player: any = null;

  // Parsea displayname: "EU-ES511-TAULI-intercom-2"
  // → strCode: "EU-ES511-TAULI", cameraId: "intercom-2"
  protected readonly videoUrl = computed<string | null>(() => {
    const call = this.incomingCall();
    if (!call?.displayname) return null;
    const clean = decodeURIComponent(call.displayname).replace(/"/g, '').trim();
    const parts = clean.split('-').map(p => p.trim());
    if (parts.length < 4) return null;
    const strCode = parts.slice(0, 3).join('-');
    const cameraId = parts.slice(3).join('-');
    return `https://local.next64.com/${strCode}/video/${cameraId}`;
  });

  constructor(protected readonly voip: VoipService) {
    effect(() => {
      const el = this.audioRef();
      const stream = this.remoteStream();
      if (!el) return;
      el.nativeElement.srcObject = stream;
      if (stream) el.nativeElement.play().catch(console.error);
    });

    effect(() => {
      const el = this.ringtoneRef()?.nativeElement;
      if (!el) return;
      if (this.callState() === 'ringing') {
        el.play()
          .then(() => console.log('[voip] ringtone playing'))
          .catch(err => console.warn('[voip] ringtone blocked by autoplay policy:', err));
      } else {
        el.pause();
        el.currentTime = 0;
      }
    });

    effect(() => {
      const url = this.videoUrl();
      const container = this.videoContainer();
      const state = this.callState();
      const isActive = state === 'ringing' || state === 'connected';

      if (isActive && url && container && !this.player) {
        this.player = new RTSPtoWEBPlayer({ parentElement: container.nativeElement });
        Promise.resolve(this.player.load(url)).catch(console.error);
      } else if (!isActive || !container) {
        this.destroyPlayer();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyPlayer();
  }

  private destroyPlayer(): void {
    if (this.player) {
      try { this.player.destroy(); } catch {}
      this.player = null;
    }
  }
}
